import { assetUrl } from "@/config";

// The inbound-message chime.
//
// This is the *in-app* sound: it plays when a message lands while the app is on
// screen, and behind a web notification, which the browser Notification API
// cannot give a custom sound of its own. When the OS raises the notification
// itself — a native local notification or a OneSignal push — the sound comes
// from the notification channel instead (see docs/PUSH.md), and this module
// stays quiet so nothing doubles up.

const ENABLED_KEY = "mandoob:sound";
const SOURCE = "/wa-notification.mp3";

let element: HTMLAudioElement | null = null;
let unlocked = false;

function audio(): HTMLAudioElement | null {
  if (typeof Audio === "undefined") return null;
  if (!element) {
    element = new Audio(assetUrl(SOURCE));
    element.preload = "auto";
    // A chime, not an alarm — it plays over whatever the driver is doing.
    element.volume = 0.7;
  }
  return element;
}

// --- Autoplay unlock -------------------------------------------------------
// iOS (and Chrome's autoplay policy) only let an element play if it has played
// once inside a user gesture. A socket event is not a gesture, so the first
// touch anywhere primes the element with a muted play/pause.

function unlock(): void {
  if (unlocked) return;
  unlocked = true;
  const player = audio();
  if (!player) return;
  const restore = player.muted;
  player.muted = true;
  player
    .play()
    .then(() => {
      player.pause();
      player.currentTime = 0;
      player.muted = restore;
    })
    .catch(() => {
      player.muted = restore;
      // Still locked — the next real gesture will try again.
      unlocked = false;
    });
}

/** Arms the one-time autoplay unlock. Idempotent; call it once at boot. */
export function initSound(): void {
  if (typeof window === "undefined") return;
  const handler = () => unlock();
  window.addEventListener("pointerdown", handler, { once: true });
  window.addEventListener("touchend", handler, { once: true });
  window.addEventListener("keydown", handler, { once: true });
}

// --- User preference -------------------------------------------------------

export function soundEnabled(): boolean {
  try {
    return localStorage.getItem(ENABLED_KEY) !== "off";
  } catch {
    return true;
  }
}

export function setSoundEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(ENABLED_KEY, enabled ? "on" : "off");
  } catch {
    // storage unavailable — preference is per-session only
  }
}

// --- Playback --------------------------------------------------------------

/** Plays the chime from the top, so back-to-back messages each get one. */
export function playMessageSound(): void {
  if (!soundEnabled()) return;
  const player = audio();
  if (!player) return;
  try {
    player.currentTime = 0;
    void player.play().catch(() => undefined);
  } catch {
    // Element not ready, or autoplay still blocked.
  }
}
